import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
  Box,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useTranslation } from 'react-i18next';
import phoneCallService, { PackageCustomerSearchItem } from '../../../../services/phoneCallService';

// ============================================================================
// MANUAL PHONE / CUSTOMER SEARCH MODAL
// Paket sipariş ekranındaki "Müşteri Ara" modalı.
//  - Telefon ile arama (4-15 hane, sadece rakam)
//  - İsim ile autocomplete arama (min 2 karakter, server-side, debounced)
//  - Müşteri seçildiğinde telefonu form alanına yazıp `onSearch` tetiklenir
// ============================================================================

export interface ManualPhoneModalProps {
  open: boolean;
  onClose: () => void;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  isSearching: boolean;
  onSearch: () => void;
}

// Telefon kuralı: sadece rakam, 4-15 hane (uluslararası numaralara da izin)
const PHONE_MIN_LEN = 4;
const PHONE_MAX_LEN = 15;
const NAME_SEARCH_MIN_LEN = 2;
const NAME_SEARCH_DEBOUNCE_MS = 300;

const ManualPhoneModal: React.FC<ManualPhoneModalProps> = ({
  open,
  onClose,
  phoneNumber,
  setPhoneNumber,
  isSearching,
  onSearch,
}) => {
  const { t } = useTranslation('salesSystem');

  // İsim arama state'i
  const [nameInput, setNameInput] = useState('');
  const [nameOptions, setNameOptions] = useState<PackageCustomerSearchItem[]>([]);
  const [isNameLoading, setIsNameLoading] = useState(false);
  const [nameSearchOpen, setNameSearchOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modal kapanınca state temizle
  useEffect(() => {
    if (!open) {
      setNameInput('');
      setNameOptions([]);
      setIsNameLoading(false);
      setNameSearchOpen(false);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    }
  }, [open]);

  // Debounced isim araması
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const trimmed = nameInput.trim();
    if (trimmed.length < NAME_SEARCH_MIN_LEN) {
      setNameOptions([]);
      setIsNameLoading(false);
      return;
    }

    setIsNameLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await phoneCallService.searchPackageCustomers(trimmed, 20);
        setNameOptions(results);
      } catch (err) {
        console.error('[ManualPhone] İsim arama hatası:', err);
        setNameOptions([]);
      } finally {
        setIsNameLoading(false);
      }
    }, NAME_SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [nameInput]);

  const handleClose = () => {
    if (!isSearching) {
      onClose();
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sadece rakam; uluslararası numaralara da izin (4-15 hane serbest)
    const value = e.target.value.replace(/\D/g, '').slice(0, PHONE_MAX_LEN);
    setPhoneNumber(value);
  };

  const isPhoneValid = phoneNumber.length >= PHONE_MIN_LEN && phoneNumber.length <= PHONE_MAX_LEN;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isPhoneValid && !isSearching) {
      onSearch();
    }
  };

  // Autocomplete'ten müşteri seçildiğinde telefonu yaz + aramayı başlat
  const handleSelectCustomer = (
    _event: React.SyntheticEvent,
    value: PackageCustomerSearchItem | string | null,
  ) => {
    if (!value || typeof value === 'string') return;
    const targetPhone = (value.mobilePhone || value.phone || '').replace(/\D/g, '').slice(0, PHONE_MAX_LEN);
    if (!targetPhone || targetPhone.length < PHONE_MIN_LEN) {
      return;
    }
    setPhoneNumber(targetPhone);
    setNameInput('');
    setNameOptions([]);
    setNameSearchOpen(false);
    // Bir tick sonra aramayı tetikle ki setPhoneNumber state'i yansısın
    setTimeout(() => {
      onSearch();
    }, 0);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonSearchIcon color="primary" />
        {t('orderManagement.manualPhone.titleSearch', 'Müşteri Ara')}
      </DialogTitle>

      <DialogContent>
        {/* ───────── İsim ile arama ───────── */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t(
              'orderManagement.manualPhone.nameSearchHelper',
              'Müşterinin adıyla arayın (en az 2 harf)',
            )}
          </Typography>
          <Autocomplete
            freeSolo
            options={nameOptions}
            loading={isNameLoading}
            open={nameSearchOpen && nameInput.trim().length >= NAME_SEARCH_MIN_LEN}
            onOpen={() => setNameSearchOpen(true)}
            onClose={() => setNameSearchOpen(false)}
            inputValue={nameInput}
            onInputChange={(_e, newValue, reason) => {
              if (reason === 'reset') return;
              setNameInput(newValue);
            }}
            onChange={handleSelectCustomer}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const ph = option.mobilePhone || option.phone || '';
              return `${option.customerName}${ph ? ` — ${ph}` : ''}`;
            }}
            isOptionEqualToValue={(opt, val) =>
              typeof opt !== 'string' && typeof val !== 'string' && opt.accountId === val.accountId
            }
            noOptionsText={
              nameInput.trim().length < NAME_SEARCH_MIN_LEN
                ? t('orderManagement.manualPhone.nameMinChars', 'En az 2 harf yazın')
                : isNameLoading
                  ? t('orderManagement.manualPhone.searching', 'Aranıyor…')
                  : t('orderManagement.manualPhone.noCustomerFound', 'Müşteri bulunamadı')
            }
            renderOption={(props, option) => {
              if (typeof option === 'string') return null;
              const phoneText = option.mobilePhone || option.phone || '—';
              return (
                <Box component="li" {...props} key={option.accountId} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {option.customerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {phoneText}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={t('orderManagement.manualPhone.namePlaceholder', 'Ad Soyad…')}
                disabled={isSearching}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isNameLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('orderManagement.manualPhone.or', 'veya')}
          </Typography>
        </Divider>

        {/* ───────── Telefon ile arama ───────── */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t(
            'orderManagement.manualPhone.phoneSearchHelper',
            'Telefon numarası ile arayın (sadece rakam)',
          )}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label={t('orderManagement.manualPhone.label')}
          placeholder={t('orderManagement.manualPhone.placeholder')}
          value={phoneNumber}
          onChange={handlePhoneChange}
          inputProps={{
            maxLength: PHONE_MAX_LEN,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          disabled={isSearching}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          helperText={
            phoneNumber.length > 0 && !isPhoneValid
              ? t(
                  'orderManagement.manualPhone.phoneRange',
                  `Telefon ${PHONE_MIN_LEN}-${PHONE_MAX_LEN} hane arasında olmalı`,
                )
              : ' '
          }
          error={phoneNumber.length > 0 && !isPhoneValid}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isSearching}>
          {t('orderManagement.manualPhone.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={onSearch}
          disabled={isSearching || !isPhoneValid}
          startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
        >
          {isSearching
            ? t('orderManagement.manualPhone.actions.searching')
            : t('orderManagement.manualPhone.actions.search')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualPhoneModal;
